
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useLanguage } from 'app/contexts/LanguageContext';
import useTranslations from '../transalation/TranslationUtils';
import { useSelector } from "react-redux";
import { DEFAULT_IP } from "app/utils/constant";
import { Snackbar } from "@mui/material";

const UserManagementNew = ({
  show,
  handleClose,
  modalTitle,
  userData,
  fetchUsers,
}) => {
  const roles = useSelector((state) => state.rdxMain?.roleList || []);
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState("");
  const [available, setAvailable] = useState(true);
  const [errorObj, setErrorObj] = useState({ msg: "", isOpen: false });
  const [successObj, setSuccessObj] = useState({ msg: "", isOpen: false });
  const [showPassword, setShowPassword] = useState(false);
  const { availableuser } = useTranslations();

  // Populate form fields if editing an existing user
  useEffect(() => {
    if (userData.role_id) {
      setAdminName(userData.admin_name || "");
      setAdminEmail(userData.admin_email || "");
      setPassword(""); // Don't populate the password field for editing
      setRoleId(userData.role_id || "");
      setAvailable(userData.available || false);
    } else {
      setAdminName("");
      setAdminEmail("");
      setPassword("");
      setRoleId("");
      setAvailable(true);
    }
  }, [userData]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = () => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(password)) {
      return "Password must be at least 8 characters long and include at least 1 lowercase letter, 1 uppercase letter, and 1 number.";
    }
    return "";
  };

  const handleSave = async () => {
    if (!validateEmail(adminEmail)) {
      setErrorObj({ msg: "Please enter a valid email address.", isOpen: true });
      return;
    }

    if (!roleId) {
      setErrorObj({ msg: "Please select a role.", isOpen: true });
      return;
    }

    if (!(userData.role_id) && !password) {
      setErrorObj({ msg: "Please enter a password.", isOpen: true });
      return;
    }

    if (!(userData.role_id)) {
      const validationMessage = validatePassword();
      if (validationMessage) {
        setErrorObj({ msg: validationMessage, isOpen: true });
        return;
      }
    }

    const token = localStorage.getItem("authToken");
    const apiEndpoint = (userData.role_id)
      ? DEFAULT_IP + "/webapp/memportal/updateadmin"
      : DEFAULT_IP + "/webapp/memportal/addadmin";

    const requestData = {
      role_id: roleId,
      admin_name: adminName,
      admin_email: adminEmail,
      available,
      ...((userData.role_id)
        ? { admin_id: userData.admin_id, admin_profile: "" } // For editing
        : { password }), // Include password only for adding
    };

    try {
      const response = await axios.post(apiEndpoint, requestData, {
        headers: {
          Token: token,
          "Content-Type": "application/json",
        },
      });

      if (response.data.code === 1) {
        let msg = (userData.role_id) ? "User updated successfully!" : "User added successfully!";
        setSuccessObj({ msg: msg, isOpen: true });
        handleClose();
        fetchUsers && fetchUsers();
      } else {
        const errorMsg =
          response.data?.message_en ||
          "Failed to save user. Please try again.";
        setErrorObj({ msg: errorMsg, isOpen: true });
      }
    } catch (error) {
      console.error("Error saving user:", error);
      setErrorObj({ msg: "Failed to save user. Please try again.", isOpen: true });
    }
  };
  const { selectedLanguage } = useLanguage();
  const getRoleName = (role) => {
    return selectedLanguage === 'zh' ? role.role_name : role.role_name_en; // Assuming role_name_zh and role_name_en exist in the role data
  };
  return (
    <React.Fragment>
      <Modal show={show} onHide={handleClose} centered size="md">
        <Modal.Body>
          <Form>
            <h4 className="maintitle">User Management</h4>

            {/* Admin Name */}
            <Form.Group controlId="formAdminName" className="floating-label-group">
              <Form.Control
                type="text"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                className="floating-input"
                required
              />
              <Form.Label className="floating-label">User Name</Form.Label>
            </Form.Group>

            {/* Admin Email */}
            <Form.Group controlId="formAdminEmail" className="floating-label-group">
              <Form.Control
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="floating-input"
                required
              />
              <Form.Label className="floating-label">User Email</Form.Label>
            </Form.Group>

            {/* Password */}
            {!(userData.role_id) && (
              <Form.Group controlId="formPassword" className="floating-label-group" style={{ position: 'relative' }}>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="floating-input"
                  required
                />
                <Form.Label className="floating-label">Password</Form.Label>
                <div
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '10px',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#6c757d',
                  }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
              </Form.Group>
            )}

            {/* Role Dropdown */}
            <Form.Group controlId="formRoleId" className="floating-label-group">
              <Form.Select value={roleId} onChange={(e) => setRoleId(e.target.value)} className="floating-input" required>
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role.role_id} value={role.role_id}>
                    {getRoleName(role)} {/* Display role name based on selected language */}
                  </option>
                ))}
              </Form.Select>
              <Form.Label className="floating-label">Role</Form.Label>
            </Form.Group>

            {/* Available Checkbox */}
            <Form.Group controlId="formAvailable" className="floating-label-group mb-2">
              <Form.Check
                type="checkbox"
                label={availableuser()}
                checked={available}
                onChange={(e) => setAvailable(e.target.checked)}
              />
            </Form.Group>

            {/* Save Button */}
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={successObj.isOpen}
        autoHideDuration={1500}
        onClose={() => { setSuccessObj(prevState => ({ ...prevState, isOpen: false })); }}>
        <Alert variant="success">{successObj.msg}</Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={errorObj.isOpen}
        autoHideDuration={1500}
        onClose={() => { setErrorObj(prevState => ({ ...prevState, isOpen: false })); }}>
        <Alert variant="danger">{errorObj.msg}</Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default UserManagementNew;
